import { useQuery } from '@apollo/client/react';
import { usePostHog } from '@posthog/react/slim';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import { GetJobPositionsFeaturesDocument } from '@pflegia/data-access/job-board.generated';
import { getBrowserPosthogDistinctId } from '@pflegia/feature-flags/utils/helpers/get-posthog-distinct-id';
import { JobPositionFeatureDomain } from '@pflegia/types/common/enums/job-position-feature-domain.enum';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { useJobPositionsFeaturesStore } from '@pflegia/ui/stores/job-positions-features.store';

import { useRouterHelper } from '@shared/hooks/use-router-helper';

const subscribeToHydration = () => () => undefined;
const getClientHydrationSnapshot = () => true;
const getServerHydrationSnapshot = () => false;

export const useJobPositionsFeatures = (jobPositionIds: string[] = []) => {
  const phClient = usePostHog();
  const authenticated = useAuth();
  const { isPraxia } = useFlavorFromRoute();
  const { isJobBoard } = useRouterHelper();
  const isHydrated = useSyncExternalStore(subscribeToHydration, getClientHydrationSnapshot, getServerHydrationSnapshot);

  const jobPositionIdRef = useRef<string[]>(jobPositionIds);
  const [isCompleted, setIsCompleted] = useState(false);

  const featureDomain = useMemo(
    () => (isJobBoard ? JobPositionFeatureDomain.JobBoard : JobPositionFeatureDomain.JobListings),
    [isJobBoard],
  );

  const postHogDistinctId = getBrowserPosthogDistinctId(phClient?.get_distinct_id?.());

  const isQuerySkippable = useMemo(
    () => isPraxia || jobPositionIds.length === 0 || (!authenticated && (!isHydrated || !postHogDistinctId)),
    [authenticated, isHydrated, postHogDistinctId, isPraxia, jobPositionIds.length],
  );

  useEffect(() => {
    if (jobPositionIds?.every(item => jobPositionIdRef?.current?.includes(item))) {
      return;
    }
    setIsCompleted(false);
    jobPositionIdRef.current = jobPositionIds;
  }, [jobPositionIds]);

  const variables = useMemo(
    () => ({
      featureDomain,
      jobPositionIds,
      userId: authenticated ? undefined : postHogDistinctId,
    }),
    [authenticated, featureDomain, jobPositionIds, postHogDistinctId],
  );

  const { [featureDomain]: featureByJobPositionId, set } = useJobPositionsFeaturesStore();
  const { data, loading } = useQuery(GetJobPositionsFeaturesDocument, {
    variables,
    skip: isQuerySkippable,
  });
  useEffect(() => {
    if (!data) return;
    const { features } = data;
    set(
      featureDomain,
      features.reduce(
        (acc, feature) => ({
          ...acc,
          [feature.jobPositionId]: {
            hasFastResponse: feature.hasFastResponse,
            hasTopSalary: feature.hasTopSalary,
            isNewChance: feature.isNewChance,
          },
        }),
        {},
      ),
    );
    setIsCompleted(true);
  }, [data, featureDomain, set]);

  return {
    featureByJobPositionId,
    loading,
    isCompleted,
  };
};
